import { useEffect, useMemo, useState, useCallback } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  addDays,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
  isBefore,
  parseISO,
  format,
} from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Loader2,
  Bell,
  Trash2,
  Pencil,
  Cake,
  Repeat,
  CalendarDays,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export const CATEGORY_META = {
  family: { label: "Family", color: "#D6C3A5" },
  friends: { label: "Friends", color: "#CFA6AA" },
};
const RECURRENCE_OPTIONS = [
  { v: "one_time", l: "One-time" },
  { v: "weekly", l: "Weekly" },
  { v: "monthly", l: "Monthly" },
  { v: "yearly", l: "Yearly" },
  { v: "birthday", l: "Birthday" },
];
const RECURRENCE_LABEL = Object.fromEntries(RECURRENCE_OPTIONS.map((o) => [o.v, o.l]));
const REMINDER_OPTIONS = [
  { v: "on_day", l: "On the day" },
  { v: "1_day", l: "1 day before" },
  { v: "1_week", l: "1 week before" },
];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const inputCls =
  "w-full px-3 py-2 rounded-md border border-input bg-white/70 text-sm focus:outline-none focus:border-foreground/40 transition-colors duration-200";

function occursOn(ev, day) {
  const base = parseISO(ev.date);
  const baseDay = startOfDay(base);
  switch (ev.recurrence) {
    case "one_time":
      return isSameDay(base, day);
    case "weekly":
      return !isBefore(day, baseDay) && day.getDay() === base.getDay();
    case "monthly":
      return !isBefore(day, baseDay) && day.getDate() === base.getDate();
    case "yearly":
    case "birthday":
      return day.getMonth() === base.getMonth() && day.getDate() === base.getDate();
    default:
      return false;
  }
}

function RecurrenceIcon({ recurrence, className }) {
  if (recurrence === "birthday") return <Cake className={className} />;
  if (recurrence === "one_time") return <CalendarDays className={className} />;
  return <Repeat className={className} />;
}

const emptyForm = (dateStr) => ({
  title: "",
  category: "family",
  date: dateStr,
  recurrence: "one_time",
  note: "",
  reminders: [],
});

function EventDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (open) setForm(initial);
  }, [open, initial]);

  const toggleReminder = (v) =>
    setForm((f) => ({
      ...f,
      reminders: f.reminders.includes(v)
        ? f.reminders.filter((r) => r !== v)
        : [...f.reminders, v],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    setBusy(true);
    const payload = {
      title: form.title.trim(),
      category: form.category,
      date: form.date,
      recurrence: form.recurrence,
      note: form.note?.trim() || null,
      reminders: form.reminders,
    };
    try {
      if (isEdit) await api.put(`/calendar/events/${initial.id}`, payload);
      else await api.post("/calendar/events", payload);
      toast.success(isEdit ? "Event updated" : "Event added");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="calendar-event-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit event" : "New event"}</DialogTitle>
          <DialogDescription>
            Plan a birthday, recurring or one-time event and optionally set reminders.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Title</label>
            <input
              className={inputCls}
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Dad's birthday"
              data-testid="event-title-input"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs text-muted-foreground mb-1.5">Category</label>
            <div className="flex gap-2">
              {Object.entries(CATEGORY_META).map(([key, meta]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, category: key }))}
                  data-testid={`event-category-${key}`}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium border transition-colors duration-200 ${
                    form.category === key
                      ? "text-[#2C2C2C] border-transparent"
                      : "bg-white/70 text-foreground/70 border-input hover:border-foreground/40"
                  }`}
                  style={form.category === key ? { backgroundColor: meta.color } : {}}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />
                  {meta.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Date</label>
              <input
                type="date"
                className={inputCls}
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                data-testid="event-date-input"
              />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Repeats</label>
              <select
                className={inputCls}
                value={form.recurrence}
                onChange={(e) => setForm((f) => ({ ...f, recurrence: e.target.value }))}
                data-testid="event-recurrence-select"
              >
                {RECURRENCE_OPTIONS.map((o) => (
                  <option key={o.v} value={o.v}>{o.l}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-muted-foreground mb-1.5">Notification reminders</label>
            <div className="flex flex-wrap gap-2">
              {REMINDER_OPTIONS.map((o) => {
                const on = form.reminders.includes(o.v);
                return (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => toggleReminder(o.v)}
                    data-testid={`event-reminder-${o.v}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors duration-200 ${
                      on
                        ? "bg-foreground text-background border-foreground"
                        : "bg-white/70 text-foreground/70 border-input hover:border-foreground/40"
                    }`}
                  >
                    <Bell className="w-3 h-3" /> {o.l}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs text-muted-foreground mb-1">Note (optional)</label>
            <textarea
              className={`${inputCls} min-h-[70px] resize-y`}
              value={form.note || ""}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              data-testid="event-note-input"
            />
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 rounded-md text-foreground/80 hover:bg-secondary transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-foreground text-background font-medium hover:bg-foreground/90 disabled:opacity-60 transition-colors duration-200"
              data-testid="event-save-btn"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {isEdit ? "Save" : "Add event"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CalendarBoard() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [selected, setSelected] = useState(startOfDay(new Date()));
  const [show, setShow] = useState({ family: true, friends: true });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitial, setDialogInitial] = useState(emptyForm(format(new Date(), "yyyy-MM-dd")));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/calendar/events");
      setEvents(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visibleEvents = useMemo(
    () => events.filter((e) => show[e.category]),
    [events, show]
  );

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    const out = [];
    let d = start;
    while (!isBefore(end, d)) {
      out.push(d);
      d = addDays(d, 1);
    }
    return out;
  }, [month]);

  const eventsOn = useCallback(
    (day) => visibleEvents.filter((e) => occursOn(e, day)),
    [visibleEvents]
  );

  const selectedEvents = useMemo(() => eventsOn(selected), [eventsOn, selected]);

  const openNew = (day) => {
    setDialogInitial(emptyForm(format(day, "yyyy-MM-dd")));
    setDialogOpen(true);
  };

  const openEdit = (ev) => {
    setDialogInitial({ ...ev, note: ev.note || "" });
    setDialogOpen(true);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/calendar/events/${id}`);
      setEvents((es) => es.filter((e) => e.id !== id));
      toast.success("Event deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="calendar-board">
      {/* toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMonth((m) => subMonths(m, 1))}
            className="p-2 rounded-md border border-input bg-white/70 hover:border-foreground/40 transition-colors duration-200"
            data-testid="calendar-prev-month"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h2 className="font-serif text-2xl text-foreground min-w-[190px] text-center" data-testid="calendar-month-label">
            {format(month, "MMMM yyyy")}
          </h2>
          <button
            onClick={() => setMonth((m) => addMonths(m, 1))}
            className="p-2 rounded-md border border-input bg-white/70 hover:border-foreground/40 transition-colors duration-200"
            data-testid="calendar-next-month"
            aria-label="Next month"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              const today = startOfDay(new Date());
              setMonth(startOfMonth(today));
              setSelected(today);
            }}
            className="ml-1 px-3 py-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors duration-200"
            data-testid="calendar-today-btn"
          >
            Today
          </button>
        </div>

        <div className="flex items-center gap-2">
          {Object.entries(CATEGORY_META).map(([key, meta]) => (
            <button
              key={key}
              onClick={() => setShow((s) => ({ ...s, [key]: !s[key] }))}
              data-testid={`calendar-toggle-${key}`}
              aria-pressed={show[key]}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all duration-200 ${
                show[key]
                  ? "border-transparent text-[#2C2C2C]"
                  : "bg-white/70 text-muted-foreground/60 border-input line-through"
              }`}
              style={show[key] ? { backgroundColor: meta.color } : {}}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />
              {meta.label} events
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* month grid */}
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-4 md:p-5">
          <div className="grid grid-cols-7 mb-2">
            {WEEKDAYS.map((w) => (
              <div key={w} className="text-center text-[11px] uppercase tracking-[0.12em] text-muted-foreground py-1">
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((day) => {
              const inMonth = isSameMonth(day, month);
              const isSel = isSameDay(day, selected);
              const isToday = isSameDay(day, new Date());
              const dayEvents = eventsOn(day);
              return (
                <button
                  key={day.toISOString()}
                  onClick={() => setSelected(startOfDay(day))}
                  onDoubleClick={() => openNew(day)}
                  data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                  className={`relative min-h-[68px] rounded-lg p-1.5 text-left border transition-colors duration-200 ${
                    isSel
                      ? "border-foreground bg-secondary"
                      : "border-transparent hover:bg-secondary/50"
                  } ${inMonth ? "" : "opacity-40"}`}
                >
                  <span
                    className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs ${
                      isToday ? "bg-foreground text-background font-semibold" : "text-foreground"
                    }`}
                  >
                    {format(day, "d")}
                  </span>
                  <div className="mt-1 space-y-0.5">
                    {dayEvents.slice(0, 2).map((e) => (
                      <div
                        key={e.id}
                        className="flex items-center gap-1 text-[10px] leading-tight text-foreground/80 truncate"
                      >
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: CATEGORY_META[e.category].color }} />
                        <span className="truncate">{e.title}</span>
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <div className="text-[10px] text-muted-foreground">+{dayEvents.length - 2} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* selected-day panel */}
        <div className="rounded-xl border border-border bg-card p-5" data-testid="calendar-day-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                {format(selected, "EEEE")}
              </p>
              <p className="font-serif text-2xl text-foreground">{format(selected, "MMMM d")}</p>
            </div>
            <button
              onClick={() => openNew(selected)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors duration-200"
              data-testid="calendar-add-event-btn"
            >
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>

          {selectedEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground italic py-6 text-center" data-testid="calendar-day-empty">
              Nothing planned. Double-click a day or hit Add.
            </p>
          ) : (
            <div className="space-y-2">
              {selectedEvents.map((e) => (
                <div
                  key={e.id}
                  className="group rounded-lg border border-border/70 p-3"
                  style={{ borderLeft: `3px solid ${CATEGORY_META[e.category].color}` }}
                  data-testid={`calendar-event-${e.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-foreground flex items-center gap-1.5">
                        <RecurrenceIcon recurrence={e.recurrence} className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate">{e.title}</span>
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {CATEGORY_META[e.category].label} · {RECURRENCE_LABEL[e.recurrence]}
                      </p>
                      {e.note && <p className="text-sm text-foreground/70 mt-1.5">{e.note}</p>}
                      {e.reminders.length > 0 && (
                        <div className="flex items-center gap-1 mt-1.5 text-[11px] text-muted-foreground">
                          <Bell className="w-3 h-3" />
                          {e.reminders
                            .map((r) => REMINDER_OPTIONS.find((o) => o.v === r)?.l)
                            .join(", ")}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 opacity-60 md:opacity-0 md:group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200">
                      <button onClick={() => openEdit(e)} className="p-1.5 text-muted-foreground hover:text-foreground" data-testid={`calendar-edit-${e.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(e.id)} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid={`calendar-delete-${e.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <EventDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={dialogInitial} onSaved={load} />
    </div>
  );
}
